// Layer 4 (UI): the plant model (`/plant-model`), rebuilt to the locked comp
// `.impeccable/mocks/decision/plant-model.html` — variant A · Flow strip,
// picked 25 Sep (polish.P11 #665), with B's circuit brackets on the Process
// graph view and C's sample points hosted on the stage that takes them.
// Page order (DESIGN.md Advisory standard): header on bare canvas · the GM
// band (GM lens) · the view toggle · the flow strip (or the process graph)
// · one 8/4 row (the selected stage · the hardness pair) · the stage's depth
// as one tab set. The strip is the page's tab bar: its selected cell drives
// the panel and the tabs. Every figure reads an existing engine or transform.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Play, RotateCcw } from 'lucide-react';
import {
  HelpAnchor,
  Pill,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
} from '@/design-system';
import { usePersona } from '@/app/providers';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { SURFACE_PAGE_TITLE_LARGE } from '@/shared/ui-class';
import { defaultRollupLensForPersona } from '@/versions/v1/shared/rollup-lens';
import { AskAssistant } from '@/versions/v1/shared/AskAssistant';
import { projectTileBySlug } from '@/versions/v1/shared/project-seed';
import { projectSidebarHref } from '@/versions/v1/shared/project-sidebar-nav';
import type { ScenarioPhase } from '@/versions/v1/schema/scenario';
import type { AccountingPeriod } from '@/versions/v1/schema/metal-accounting';
import {
  SCENARIO_ADVANCE_LABEL,
  SCENARIO_PHASE_LABEL,
  SCENARIOS,
  isWhatIfDefault,
} from '@/versions/v1/shared/ScenarioEngine';
import { useScenario } from '@/versions/v1/shared/ScenarioProvider';
import { osaCoverageLabel } from '@/versions/v1/modules/quality/Scene.transform';
import { RECIRCULATING_LOOPS, SAMPLE_POINTS, STREAMS } from '@/versions/v1/shared/bunker-hill-seed';
import { plantNode, subprocessForNodeId } from '@/versions/v1/shared/bunker-hill-plant-nodes';
import { readingsAt } from '@/versions/v1/shared/series.derive';
import {
  applySamplePhase,
  applyUnits,
  applyUtilization,
  bunkerHillConstraints,
  bunkerHillSamplePoints,
  bunkerHillSubprocesses,
  bunkerHillUnits,
  bunkerHillUtilization,
  constraintRows,
  defaultStripNodeId,
  grindOperatingCurve,
  hardnessPair,
  ingestionCopy,
  insightTable,
  nodeDetail,
  nodeHumanName,
  nodeSummary,
  plantStrip,
  sampleChips,
} from './Scene.transform';
import { BackToTop } from './BackToTop';
import { ProcessGraph } from './ProcessGraph';
import { SCENARIO_GRAPH_OPTIONS, WHAT_IF_DEFAULTS, whatIfDerived } from './ProcessGraph.transform';
import { nodeDisplays } from './NodeDisplays.transform';
import { periodWindows } from './periodBalance';
import { circuitsView, CIRCUITS } from './Circuits.transform';
import { RollupStrip } from './RollupStrip';
import { gmRollup } from './RollupStrip.transform';
import { PlantFlowStrip } from './PlantFlowStrip';
import { circuitForStage, flowCellIdForParam, plantFlowCells } from './PlantFlowStrip.transform';
import { StagePanel } from './StagePanel';
import { stageFacts, stageRead, stageRows, stageSamples } from './StagePanel.transform';
import { HardnessPair } from './HardnessPair';
import { StageDepth } from './StageDepth';
import { resolveStageTab, stageTabs, stageUnitRows, type StageTabId } from './StageDepth.transform';

const PHASE_PILL_TONE: Record<ScenarioPhase, 'default' | 'warning' | 'danger'> = {
  idle: 'default',
  provisional: 'warning',
  confirmed: 'danger',
};

/** A pill group with the chosen option filled in ink (the Downtime window
 *  segment's recipe; comp `.seg.sm`). */
function Segment<T extends string>({
  label,
  role = 'group',
  options,
  value,
  onChange,
  disabled = false,
  disabledTitle,
}: {
  label: string;
  role?: 'group' | 'tablist';
  options: readonly { id: T; label: string }[];
  value: T;
  onChange: (next: T) => void;
  disabled?: boolean;
  disabledTitle?: string;
}) {
  return (
    <div
      role={role}
      aria-label={label}
      className="inline-flex shrink-0 items-center gap-0.5 rounded-full border border-[var(--hairline-strong)] bg-[var(--surface-1)] p-[3px]"
    >
      {options.map((option) => {
        const active = option.id === value;
        return (
          <button
            key={option.id}
            type="button"
            {...(role === 'tablist'
              ? { role: 'tab', 'aria-selected': active }
              : { 'aria-pressed': active && !disabled })}
            disabled={disabled}
            title={disabled ? disabledTitle : undefined}
            onClick={() => onChange(option.id)}
            className={cn(
              'whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium transition-colors duration-150',
              active && !disabled
                ? 'bg-ink-primary text-[var(--surface-2)]'
                : 'text-ink-secondary hover:bg-[var(--surface-2)] hover:text-ink-primary',
              disabled ? 'cursor-not-allowed opacity-40 hover:bg-transparent' : '',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

const PHASE_OPTIONS = SCENARIO_GRAPH_OPTIONS.map((option) => ({ id: option.phase, label: option.label }));
const VIEW_OPTIONS = [
  { id: 'circuits' as const, label: 'Circuits' },
  { id: 'graph' as const, label: 'Process graph' },
];

export function PlantModelScene() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const maActive = searchParams.get('layer') === 'ma';
  const periodParam = searchParams.get('period');
  const period: AccountingPeriod =
    periodParam === 'shift' || periodParam === 'mtd' || periodParam === 'day' ? periodParam : 'day';
  const project = projectTileBySlug(projectId);
  const plantName = project?.name ?? 'this plant';
  const copy = ingestionCopy(plantName);
  const { phase, state, diagnosis, advance, set: setScenarioPhase, toggleScenario, setWhatIf } = useScenario();
  const { persona } = usePersona();
  // #169 + Miles' steer: the persona picker IS the lens switch — no on-surface tabs.
  const lens = defaultRollupLensForPersona(persona);

  // `?circuit=<sp-id|CircuitId>` selects the strip cell and the panel together
  // (polish.P11); `?view=graph` and `?layer=ma` open the process graph.
  const [selectedNodeId, setSelectedNodeId] = useState<string>(
    () => flowCellIdForParam(searchParams.get('circuit')) ?? defaultStripNodeId(),
  );
  const [canvasView, setCanvasView] = useState<'circuits' | 'graph'>(() =>
    searchParams.get('view') === 'graph' || maActive ? 'graph' : 'circuits',
  );
  const deepLinkReading = searchParams.get('reading');
  const deepLinkConstraintId = deepLinkReading?.startsWith('rd-c-') ? deepLinkReading.slice(3) : undefined;
  const [requestedTab, setRequestedTab] = useState<StageTabId | null>(() =>
    maActive ? 'balance' : deepLinkConstraintId ? 'window' : null,
  );

  // paul-email.A2 #247 (MS-2): `?node=` from the stage ladder selects the
  // node's owning stage; `?reading=rd-c-*` opens its operating-window row.
  useEffect(() => {
    const nodeParam = searchParams.get('node');
    if (!nodeParam || !plantNode(nodeParam)) return;
    setSelectedNodeId(subprocessForNodeId(nodeParam) ?? nodeParam);
    document.getElementById('plant-model-stage-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (deepLinkConstraintId) {
      const scrollConstraint = (attempt = 0) => {
        const row = document.getElementById(`plant-model-constraint-${deepLinkConstraintId}`);
        if (row) {
          row.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        if (attempt < 24) window.requestAnimationFrame(() => scrollConstraint(attempt + 1));
      };
      window.requestAnimationFrame(() => scrollConstraint());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const readings = useMemo(() => readingsAt(state), [state]);
  const units = useMemo(() => applyUnits(bunkerHillUnits()), []);
  const subprocesses = bunkerHillSubprocesses();
  const constraints = bunkerHillConstraints();
  const ranks = useMemo(() => applyUtilization(bunkerHillUtilization(), phase, state), [phase, state]);
  const samplePoints = useMemo(
    () => sampleChips(applySamplePhase(bunkerHillSamplePoints(), diagnosis, state)),
    [diagnosis, state],
  );
  const graphSamplePoints = useMemo(() => applySamplePhase(SAMPLE_POINTS, diagnosis, state), [diagnosis, state]);
  const strip = useMemo(
    () => plantStrip(ranks, STREAMS, RECIRCULATING_LOOPS, subprocesses, units, readings),
    [ranks, subprocesses, units, readings],
  );
  const pair = useMemo(() => hardnessPair(phase, state), [phase, state]);
  const grindCurve = useMemo(
    () => (selectedNodeId === 'sp-grind' ? grindOperatingCurve(state) : null),
    [selectedNodeId, state],
  );
  const detail = useMemo(
    () => nodeDetail(selectedNodeId, { subprocesses, constraints, units, ranks }, phase),
    [selectedNodeId, subprocesses, constraints, units, ranks, phase],
  );
  const constraintTable = useMemo(
    () => constraintRows(detail.constraints, readings, state),
    [detail.constraints, readings, state],
  );
  const looksRows = useMemo(() => insightTable(detail.insightGroups), [detail.insightGroups]);
  const summary = useMemo(
    () =>
      nodeSummary(selectedNodeId, {
        phase,
        ranks,
        constraintRows: constraintTable,
        insightGroups: detail.insightGroups,
        state,
      }),
    [selectedNodeId, phase, ranks, constraintTable, detail.insightGroups, state],
  );
  const openCircuitId = circuitForStage(selectedNodeId);
  const circuits = useMemo(() => circuitsView(state, { openId: openCircuitId }), [state, openCircuitId]);
  const cells = useMemo(
    () => plantFlowCells({ strip, circuits, samples: samplePoints, phase }),
    [strip, circuits, samplePoints, phase],
  );
  const whatIf = state.whatIf ?? WHAT_IF_DEFAULTS;
  const whatIfActive = !isWhatIfDefault(whatIf);
  const rollup = useMemo(
    () => (lens === 'gm' ? gmRollup(state, ranks, whatIfActive ? whatIfDerived(whatIf) : null) : null),
    [lens, state, ranks, whatIfActive, whatIf],
  );
  const read = stageRead(summary, constraintTable.length);
  const facts = stageFacts(detail.subprocess);
  const stageRowsView = useMemo(() => (circuits.open ? stageRows(circuits.open.stages) : null), [circuits.open]);
  const samples = useMemo(() => stageSamples(selectedNodeId, samplePoints), [selectedNodeId, samplePoints]);
  const unitRows = useMemo(
    () => stageUnitRows(detail.units, { phase, readings, state, nodeId: selectedNodeId }),
    [detail.units, phase, readings, state, selectedNodeId],
  );
  const groups = stageTabs({
    pane: circuits.open?.pane,
    windowRows: constraintTable.length,
    displays: nodeDisplays(selectedNodeId).length,
    looksRows: looksRows.length,
    maActive,
    streams: detail.streams.length,
    rules: detail.rules.length,
    units: unitRows.length,
    headroom: detail.ranks.length,
  });
  const tab = resolveStageTab(groups, requestedTab);

  // Q3: Adjust inputs and the metal-accounting layer live on the process
  // graph only; if either turns on while Circuits shows, follow it there.
  useEffect(() => {
    if ((maActive || whatIfActive) && canvasView !== 'graph') setCanvasView('graph');
  }, [maActive, whatIfActive, canvasView]);

  useEffect(() => {
    if (maActive) setRequestedTab('balance');
  }, [maActive]);

  const selectStage = useCallback((next: string) => {
    setSelectedNodeId(next);
    setRequestedTab(null);
  }, []);

  useEffect(() => {
    document.title = project ? `${copy.title} · ${project.name}` : `${copy.title} · OptiFlow`;
  }, [copy.title, project]);

  if (!project) {
    return (
      <SurfacePageLayout width="full">
        <SurfacePageBody>
          <SurfacePageHeader title="Plant not found" />
          <Link to="/projects" className="text-sm font-medium text-accent-700 dark:text-accent-400">
            Back to portfolio
          </Link>
        </SurfacePageBody>
      </SurfacePageLayout>
    );
  }

  const stageName = nodeHumanName(selectedNodeId, detail.subprocess);
  const silverPushOn = state.scenarios.includes('silver-push');
  const silverBundle = SCENARIOS.find((row) => row.id === 'silver-push');
  const circuitLabel = CIRCUITS.find((row) => row.subprocessId === selectedNodeId)?.label ?? null;

  return (
    <SurfacePageLayout width="full">
      <Commentable name="IngestionScene">
        <SurfacePageBody className="space-y-3.5" width="full">
          <header
            id="plant-model-page-header"
            tabIndex={-1}
            className="flex flex-wrap items-center gap-x-3 gap-y-2 outline-none"
          >
            <h1 className={cn(SURFACE_PAGE_TITLE_LARGE, 'whitespace-nowrap')}>{copy.title}</h1>
            <Pill tone={PHASE_PILL_TONE[phase]} data-phase-pill>
              {SCENARIO_PHASE_LABEL[phase]}
            </Pill>
            <span className="whitespace-nowrap rounded-full border border-dashed border-[var(--hairline-strong)] px-2 py-0.5 text-xs text-muted">
              topology inferred until P&amp;ID lands
            </span>
            <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={advance}
                aria-label={SCENARIO_ADVANCE_LABEL[phase]}
                title={SCENARIO_ADVANCE_LABEL[phase]}
                data-scenario-advance
                className="inline-flex size-[30px] shrink-0 items-center justify-center rounded-full bg-ink-primary text-[var(--surface-2)] transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)]"
              >
                {phase === 'confirmed' ? (
                  <RotateCcw className="size-3.5" aria-hidden />
                ) : (
                  <Play className="ml-px size-3" fill="currentColor" aria-hidden />
                )}
              </button>
              <Segment
                label="Scenario"
                options={PHASE_OPTIONS}
                value={phase}
                onChange={setScenarioPhase}
                disabled={whatIfActive}
                disabledTitle="Reset the what-if inputs to play the scripted scenario"
              />
              <button
                type="button"
                aria-pressed={silverPushOn}
                title={silverBundle?.description}
                onClick={() => toggleScenario('silver-push')}
                className={cn(
                  'inline-flex h-[30px] items-center whitespace-nowrap rounded-[10px] border px-3 text-xs font-medium transition-colors',
                  silverPushOn
                    ? 'border-ink-primary bg-ink-primary text-[var(--surface-2)]'
                    : 'border-[var(--hairline-strong)] bg-[var(--surface-2)] text-ink-secondary hover:text-ink-primary',
                )}
              >
                Push silver
              </button>
              <AskAssistant threadId="thread-pm-bottleneck" label="Ask about the bottleneck" compact />
              <HelpAnchor doc="ingestion/plant-model" label="Plant model" />
            </div>
          </header>

          {rollup ? <RollupStrip view={rollup} /> : null}

          <Segment
            label="Plant model canvas"
            role="tablist"
            options={VIEW_OPTIONS}
            value={canvasView}
            onChange={setCanvasView}
          />

          {canvasView === 'graph' ? (
            <ProcessGraph
              ranks={ranks}
              phase={phase}
              state={state}
              onPhaseChange={setScenarioPhase}
              onSetWhatIf={setWhatIf}
              samplePoints={graphSamplePoints}
              projectSlug={project.slug}
              lens={lens}
              selectedNodeId={selectedNodeId}
              onSelectNode={selectStage}
              period={period}
              circuitCells={cells}
            />
          ) : (
            <Commentable name="FlowStrip">
              <div>
                <PlantFlowStrip cells={cells} selectedId={selectedNodeId} onSelect={selectStage} />
              </div>
            </Commentable>
          )}

          <div
            id="plant-model-stage-panel"
            role="tabpanel"
            aria-labelledby={`plant-flow-tab-${selectedNodeId}`}
            className="grid grid-cols-12 items-start gap-3.5 scroll-mt-4"
          >
            <div className="col-span-12 min-w-0 lg:col-span-8" id="plant-model-node-detail">
              <Commentable name="NodeDetail">
                <div>
                  <StagePanel
                    stageName={stageName}
                    rulesLine={detail.rulesLine}
                    advisoryHref={`${projectSidebarHref(project.slug, 'advisory')}?scope=${selectedNodeId}`}
                    read={read}
                    facts={facts}
                    stages={stageRowsView}
                    spcClock={state.cursor.split('T')[1] ?? state.cursor}
                    samples={samples}
                    samplesMeta={osaCoverageLabel(state.cadence)}
                  />
                </div>
              </Commentable>
            </div>
            <div className="col-span-12 min-w-0 lg:col-span-4">
              <Commentable name="HardnessPair">
                <div>
                  <HardnessPair pair={pair} />
                </div>
              </Commentable>
            </div>
          </div>

          <StageDepth
            groups={groups}
            tab={tab}
            onTabChange={setRequestedTab}
            stageName={stageName}
            nodeId={selectedNodeId}
            circuitLabel={circuitLabel}
            pane={circuits.open?.pane}
            windowRows={constraintTable}
            windowHeadline={summary.windowHeadline}
            grindCurve={grindCurve}
            initialOpenChart={deepLinkConstraintId}
            looksRows={looksRows}
            looksEntries={summary.looksLikeCount}
            streams={detail.streams}
            rules={detail.rules}
            rulesLine={detail.rulesLine}
            units={unitRows}
            headroom={detail.ranks}
            balanceView={periodWindows()[period]}
          />

          <BackToTop headerId="plant-model-page-header" />
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}
